import { useSyncExternalStore, type CSSProperties } from 'react'
import { throughLabel, type Period } from '../landing'
import { SearchableSelect } from './SearchableSelect'

// Month selector for a dashboard section — mirrors the hero's picker so a section
// (Marknad) can request an earlier month instead of being pinned to the latest.
//
// MP-OS-189 (MPD-71, MPD-77) — one button per loaded month grows with every month of history
// (11 in production on 2026-10-20, one more each month).
// - Wide desktop (1024px and up): every month is a visible button, one click away (Adnan, MPD-77
//   review). The row wraps onto as many lines as it needs and never scrolls or hides a month, so
//   its height depends only on the viewport width and the number of months, never on loading or
//   the selection (the selected chip has the same size as the others).
// - Below 1024px (phones and tablets): the shared searchable picker. At 768px the wrapped buttons
//   grew the sticky bar to 6-7 lines (MPD-85, M77-1). This is the month control's own breakpoint,
//   not the nav drawer's (760px).
// Only one form is rendered (a matchMedia hook rather than two forms with one hidden by CSS),
// so the other is never in the accessibility tree or the tab order, and an open picker cannot
// linger behind a hidden form. The selection is the `value` prop either way, so a width change
// keeps it.
export const COMPACT_MONTH_QUERY = '(max-width: 1023px)'

const subscribe = (onChange: () => void) => {
  const mq = typeof window !== 'undefined' ? window.matchMedia?.(COMPACT_MONTH_QUERY) : undefined
  mq?.addEventListener('change', onChange)
  return () => mq?.removeEventListener('change', onChange)
}
const isCompact = () => (typeof window !== 'undefined' && window.matchMedia?.(COMPACT_MONTH_QUERY).matches) || false

export function MonthPicker({
  periods,
  value,
  onChange,
}: {
  periods: Period[]
  value: Period | null
  onChange: (p: Period) => void
}) {
  const compact = useSyncExternalStore(subscribe, isCompact, () => false)

  if (!periods.length) return null
  if (compact) {
    return (
      <div style={S.wrap}>
        <span style={S.label} aria-hidden>Month</span>
        <SearchableSelect
          label="Month"
          searchLabel="Search months"
          showAllLabel="Show all months"
          value={value?.value ?? ''}
          title={value?.ongoing ? `Ongoing month (${throughLabel(value)})` : undefined}
          options={periods.map((p) => ({ value: p.value, label: `${p.label}${p.ongoing ? ' · ongoing' : ''}` }))}
          onChange={(v) => {
            const next = periods.find((p) => p.value === v)
            if (next) onChange(next)
          }}
        />
      </div>
    )
  }
  return (
    <div style={S.row}>
      <span style={{ ...S.label, ...S.rowLabel }} aria-hidden>Month</span>
      <div role="group" aria-label="Month" style={S.seg}>
        {periods.map((p) => {
          const on = p.value === value?.value
          return (
            <button
              key={p.value}
              type="button"
              aria-pressed={on}
              style={{ ...S.btn, ...(on ? S.btnOn : {}) }}
              onClick={() => onChange(p)}
              title={p.ongoing ? `Ongoing month (${throughLabel(p)})` : undefined}
            >
              {p.label}
              {p.ongoing ? ' · ongoing' : ''}
            </button>
          )
        })}
      </div>
    </div>
  )
}

const S: Record<string, CSSProperties> = {
  wrap: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  label: { fontSize: 12, fontWeight: 600, color: 'var(--muted, #6a6a6a)', flex: 'none' },
  // Buttons form: the label stays on the first line; the months wrap beside it.
  row: { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16 },
  rowLabel: { paddingTop: 9 },
  seg: { display: 'flex', flexWrap: 'wrap', gap: 4, minWidth: 0, background: 'var(--brand-tint, #eef1f0)', padding: 3, borderRadius: 10 },
  btn: { border: 'none', background: 'transparent', color: 'var(--ink, #333)', fontSize: 13, fontWeight: 600, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnOn: { background: 'var(--surface, #fff)', color: 'var(--brand-dark)', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' },
}
